import { ReactNode, useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  InputBase,
  Button,
  TableSortLabel,
  Box,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@styles/all-images/icons/search.svg";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down.svg";
import { visuallyHidden } from "@mui/utils";
import CustomPagination from "./CustomPagination";
import { toast } from "react-toastify";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import ChevronDownIcon from "@styles/all-images/icons/chevron-down-grey.svg";
import CheckIcon from "@styles/all-images/icons/blue-tick.svg";
import ElipsedText from "@components/common/elipsis/ElipsedText";

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 8,
  border: "1px solid #E9EAEB",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  marginBottom: theme.spacing(2),
}));

const StyledTableCell = styled(TableCell)(() => ({
  padding: "16px 24px",
  fontSize: "14px",
  "&.MuiTableCell-head": {
    backgroundColor: "#fff",
    fontWeight: 500,
    color: "#535862",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

interface TableColumn<T> {
  header: string;
  accessor: keyof T | ((row: T) => ReactNode);
  sortable?: boolean;
  sortKey?: string;
}

interface Pagination {
  count: number;
  page: number;
  limit: number;
}

export interface CustomTableProps<T> {
  columns: TableColumn<T>[];
  forceRefresh: boolean;
  setForceRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  hideControls?: boolean;
  fetchData: (
    filters: any,
  ) => Promise<{ data: { data: T[]; meta: Pagination } }>;
  addButton?: {
    text: string;
    icon: string;
    onClick: () => void;
  };
  archiveButton?: {
    text: string;
    icon: string;
    onClick: () => void;
    isArchiveView: boolean;
  };
  filterOptions?: {
    show: boolean;
    options: string[];
    onFilterChange: (selected: string[]) => void;
    placeholder?: string;
  };
}

export default function CustomTable<T>({
  columns,
  fetchData,
  addButton,
  archiveButton,
  forceRefresh,
  setForceRefresh,
  hideControls,
  filterOptions,
}: Omit<CustomTableProps<T>, "loading">) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>({});
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (forceRefresh) {
      handleFetchData();
      setForceRefresh(false);
    }
  }, [forceRefresh]);

  useEffect(() => {
    handleFetchData();
  }, [page, rowsPerPage, searchTerm, orderBy, order]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await fetchData({
        page,
        limit: rowsPerPage,
        search: searchTerm,
        sortColumn: orderBy,
        sortDirection: order,
      });

      setData(response.data || []);
      setTotalPages(Math.ceil((response.data.meta.count || 1) / rowsPerPage));
    } catch (error: any) {
      console.error("Error fetching data:", error);
      if (Array.isArray(error?.response?.data?.message)) {
        error.response.data.message.forEach((message: string) => {
          toast.error(message);
        });
      } else {
        toast.error(
          error?.response?.data?.message ||
            "An unexpected error occurred while fetching data",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, orderBy, order, fetchData]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page on search
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRowsPerPageChange = (value: number) => {
    setRowsPerPage(value);
    setPage(1);
    handleMenuClose();
  };

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !column.sortKey) return;

    const isAsc = orderBy === column.sortKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column.sortKey);
  };

  const handleFilterSelect = (option: string) => {
    let newSelected: string[];
    if (option === "all") {
      newSelected =
        selectedFilters.length === filterOptions?.options.length
          ? []
          : [...(filterOptions?.options || [])];
    } else {
      newSelected = selectedFilters.includes(option)
        ? selectedFilters.filter((item) => item !== option)
        : [...selectedFilters, option];
    }
    setSelectedFilters(newSelected);
    filterOptions?.onFilterChange(newSelected);
  };

  const renderTableBody = useCallback(() => {
    if (loading) {
      return Array(rowsPerPage)
        .fill(0)
        .map((_, index) => (
          <StyledTableRow key={`skeleton-${index}`}>
            {columns.map((_, colIndex) => (
              <StyledTableCell key={`skeleton-cell-${colIndex}`}>
                <Skeleton animation="wave" height={24} />
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ));
    }

    return (
      <>
        {data.data?.map((row: any, rowIndex: any) => (
          <StyledTableRow key={rowIndex}>
            {columns.map((column, colIndex) => (
              <StyledTableCell key={colIndex}>
                {typeof column.accessor === "function"
                  ? column.accessor(row)
                  : String(row[column.accessor as keyof T])}
              </StyledTableCell>
            ))}
          </StyledTableRow>
        ))}
        {!loading && data.data?.length === 0 && (
          <StyledTableRow>
            <StyledTableCell
              colSpan={columns.length}
              className="tw-text-center tw-py-4"
            >
              No data found
            </StyledTableCell>
          </StyledTableRow>
        )}
      </>
    );
  }, [loading, data, columns]);

  return (
    <div className="tw-p-6">
      {!hideControls && (
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-4">
            <Paper
              component="form"
              sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: 256,
                border: "1px solid #E9EAEB",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
                <img src={SearchIcon} alt="Search" className="tw-w-5 tw-h-5" />
              </IconButton>
            </Paper>

            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-sm tw-text-gray-600">Rows per page</span>
              <Button
                onClick={handleMenuOpen}
                endIcon={
                  <img
                    src={ArrowDownIcon}
                    alt="Arrow Down"
                    className="tw-w-4 tw-h-4"
                  />
                }
                sx={{
                  border: "1px solid #E9EAEB",
                  color: "#535862",
                  textTransform: "none",
                  minWidth: "auto",
                  padding: "4px 12px",
                }}
              >
                {rowsPerPage}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                {[5, 10, 15, 20, 25].map((value) => (
                  <MenuItem
                    key={value}
                    onClick={() => handleRowsPerPageChange(value)}
                  >
                    {value}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-4">
            {filterOptions?.show && !archiveButton?.isArchiveView && (
              <div className="tw-relative tw-w-64">
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm"
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <img
                      src={FilterIcon}
                      alt="Filter Icon"
                      className="tw-w-5 tw-h-5"
                    />
                    <span className="tw-text-[#717680]">
                      {selectedFilters.length
                        ? `${selectedFilters.length} selected`
                        : filterOptions.placeholder || "Select Filter"}
                    </span>
                  </div>
                  <img
                    src={ChevronDownIcon}
                    alt="Chevron Down"
                    className={`tw-w-5 tw-h-5 tw-transition-transform ${isFilterOpen ? "tw-rotate-180" : ""}`}
                  />
                </button>

                {isFilterOpen && (
                  <div className="tw-absolute tw-z-20 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-[#F5F5F5] tw-rounded-lg tw-shadow-lg">
                    <div className="">
                      <button
                        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 hover:tw-bg-[#FAFAFA] tw-rounded"
                        onClick={() => handleFilterSelect("all")}
                      >
                        <span className="tw-text-[#717680]">Select All</span>
                        {selectedFilters.length ===
                          filterOptions.options.length && (
                          <img
                            src={CheckIcon}
                            alt="Check Icon"
                            className="tw-w-5 tw-h-5"
                          />
                        )}
                      </button>
                      {filterOptions.options.map((option) => (
                        <button
                          key={option}
                          className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 hover:tw-bg-[#FAFAFA] tw-rounded"
                          onClick={() => handleFilterSelect(option)}
                        >
                          <span className="tw-text-[#717680]">
                            <ElipsedText text={option} />
                          </span>
                          {selectedFilters.includes(option) && (
                            <img
                              src={CheckIcon}
                              alt="Check Icon"
                              className="tw-w-5 tw-h-5"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {archiveButton && (
              <button
                onClick={archiveButton.onClick}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-px-4 tw-py-2 primary-custom-button-outline font-primary"
              >
                <img
                  src={archiveButton.icon}
                  alt="Archive Icon"
                  className="tw-w-6 tw-h-6"
                />
                {archiveButton.text}
              </button>
            )}

            {addButton && !archiveButton?.isArchiveView && (
              <button
                onClick={addButton.onClick}
                className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                <img
                  src={addButton.icon}
                  alt="Add Icon"
                  className="tw-w-5 tw-h-5"
                />
                {addButton.text}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table Component */}
      <StyledTableContainer>
        <Table className="tw-rounded-lg tw-border tw-border-[#E9EAEB]">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <StyledTableCell key={index}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.sortKey}
                      direction={orderBy === column.sortKey ? order : "asc"}
                      onClick={() => handleSort(column)}
                    >
                      <span className="tw-text-[#535862] tw-bg-white tw-font-medium tw-text-sm font-primary">
                        {column.header}
                      </span>
                      {orderBy === column.sortKey ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.header
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      <CustomPagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
      />
    </div>
  );
}
